// Pure helpers used by the CIO Committee UI. Kept JSX-free so they can be
// unit-tested with jest alone (no jsdom or react-testing-library needed).

export const VERDICT_CLASS = {
  BUY: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30',
  ADD: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30',
  HOLD: 'bg-blue-500/10 text-blue-500 border-blue-500/30',
  TRIM: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
  SELL: 'bg-red-500/10 text-red-500 border-red-500/30',
  PASS: 'bg-muted text-muted-foreground border-muted-foreground/20',
};

export function classForVerdict(verdict) {
  return VERDICT_CLASS[verdict] || VERDICT_CLASS.HOLD;
}

export function complianceLabel(status) {
  switch (status) {
    case 'fail_blocked': return 'Compliance Blocked';
    case 'fail_overridden': return 'Overrides Applied';
    case 'pass':
    default: return 'Compliance Pass';
  }
}

// Group committee artifacts into { phase: [artifact, ...] }.
export function groupArtifactsByPhase(artifacts) {
  const out = {};
  for (const a of artifacts || []) {
    const k = a && a.phase ? a.phase : 'unknown';
    (out[k] = out[k] || []).push(a);
  }
  return out;
}

// Format paise as ₹ amount.
export function formatPaise(paise, opts = {}) {
  const value = (Number(paise) || 0) / 100;
  return `₹${value.toLocaleString('en-IN', {
    maximumFractionDigits: opts.decimals ?? 2,
    minimumFractionDigits: 0,
  })}`;
}

export function budgetBarColor(pct) {
  if (pct >= 100) return 'bg-red-500';
  if (pct >= 80) return 'bg-amber-500';
  return 'bg-emerald-500';
}

// Normalize ISIN: trim + uppercase. Returns '' for non-strings.
export function normalizeIsin(isin) {
  if (typeof isin !== 'string') return '';
  return isin.trim().toUpperCase();
}

// Validate ISIN: 12 chars, leading letters then alphanumerics.
export function isValidIsin(isin) {
  if (typeof isin !== 'string') return false;
  return /^[A-Z]{2}[A-Z0-9]{9}[0-9]$/.test(isin.trim().toUpperCase());
}

// Split a comma-separated list into a deduped, trimmed array.
export function commaListToArray(str, { upper = false } = {}) {
  if (typeof str !== 'string') return [];
  const out = [];
  for (const piece of str.split(',')) {
    const v = piece.trim();
    if (!v) continue;
    const k = upper ? v.toUpperCase() : v;
    if (!out.includes(k)) out.push(k);
  }
  return out;
}

// Parse a single SSE frame ("event: foo\ndata: {json}").
export function parseSSEFrame(part) {
  if (typeof part !== 'string') return null;
  const m = /^event: (\S+)\ndata: ([\s\S]+)$/m.exec(part);
  if (!m) return null;
  let payload = {};
  try { payload = JSON.parse(m[2]); } catch (_e) { /* keep empty */ }
  return { type: m[1], ...payload };
}

// Split a buffered SSE stream into { events, remainder } using \n\n delimiter.
export function flushSSEBuffer(buffer) {
  if (typeof buffer !== 'string' || !buffer) {
    return { events: [], remainder: buffer || '' };
  }
  const parts = buffer.split('\n\n');
  const remainder = parts.pop() || '';
  const events = [];
  for (const part of parts) {
    const evt = parseSSEFrame(part);
    if (evt) events.push(evt);
  }
  return { events, remainder };
}

// Color class for an SSE event row in the live progress feed.
export function eventColor(type) {
  if (!type) return 'text-muted-foreground';
  if (type.startsWith('phase')) return 'text-blue-500';
  if (type === 'agent.complete') return 'text-emerald-500';
  if (type === 'decision.ready') return 'text-amber-500 font-semibold';
  if (type === 'error' || type === 'run.error') return 'text-red-500';
  if (type === 'budget.warning') return 'text-amber-500';
  return 'text-muted-foreground';
}

// Build a stable URL fragment for a committee run page.
export function runHref(runId) {
  if (!runId) return '/public/committee';
  return `/public/committee/runs/${runId}`;
}

// Compute monthly cost summary metrics.
export function summarizeCost(data) {
  if (!data) return null;
  const used = (data.monthly_used_inr_paise || 0) / 100;
  const budget = (data.monthly_budget_inr_paise || 0) / 100;
  const pct = budget > 0 ? (used / budget) * 100 : 0;
  return {
    used,
    budget,
    pct: Number.isFinite(pct) ? Math.max(0, pct) : 0,
    barColor: budgetBarColor(pct),
    runs: data.monthly_runs || 0,
  };
}
